import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api-error";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Pencil, Search, X } from "lucide-react";
import { listPaymentMethods } from "@/lib/api";
import {
  arCreateCategory,
  arCreateClient,
  arDeleteCategory,
  arDeleteClient,
  arListCategories,
  arListClients,
  arUpdateCategory,
  arUpdateClient,
} from "@/lib/ar-api";

/**
 * Normalizza per matching case + accent-insensitive.
 * "Rossì & C." → "rossi & c."
 */
const normalize = (s) =>
  (s || "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

/**
 * Small reusable manager for AR clients and AR categories.
 * Used inside Settings as two tabs.
 */
function CrudPanel({ kind }) {
  const isClients = kind === "clients";
  const [items, setItems] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: "",
    tax_code: "",
    email: "",
    phone: "",
    default_payment_method_id: "",
  });
  // v1.60.4 — ricerca inline case/accent-insensitive (Categorie incassi + Clienti).
  const [query, setQuery] = useState("");

  const load = async () => {
    try {
      const res = isClients ? await arListClients() : await arListCategories();
      setItems(res || []);
    } catch {
      toast.error("Errore caricamento");
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  // Solo per Clienti: carichiamo l'anagrafica metodi di pagamento per il
  // dropdown "Metodo preferito" (usato come fallback in "Registra incasso").
  useEffect(() => {
    if (!isClients) return;
    (async () => {
      try {
        const pms = await listPaymentMethods({ includeArchived: false });
        setPaymentMethods(pms || []);
      } catch {
        /* non-fatal: dropdown resta vuoto */
      }
    })();
  }, [isClients]);

  const openCreate = () => {
    setEditing(null);
    setForm({
      name: "",
      tax_code: "",
      email: "",
      phone: "",
      default_payment_method_id: "",
    });
    setOpen(true);
  };
  const openEdit = (item) => {
    setEditing(item);
    setForm({
      name: item.name || "",
      tax_code: item.tax_code || "",
      email: item.email || "",
      phone: item.phone || "",
      default_payment_method_id: item.default_payment_method_id || "",
    });
    setOpen(true);
  };
  const submit = async () => {
    if (!form.name.trim()) {
      toast.error("Nome obbligatorio");
      return;
    }
    const payload = isClients
      ? {
          name: form.name,
          tax_code: form.tax_code || null,
          email: form.email || null,
          phone: form.phone || null,
          default_payment_method_id: form.default_payment_method_id || null,
        }
      : { name: form.name };
    try {
      if (editing) {
        if (isClients) await arUpdateClient(editing.id, payload);
        else await arUpdateCategory(editing.id, payload);
        toast.success("Aggiornato.");
      } else {
        if (isClients) await arCreateClient(payload);
        else await arCreateCategory(payload);
        toast.success("Creato.");
      }
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e, "Errore salvataggio"));
    }
  };
  const remove = async (item) => {
    if (!window.confirm(`Disattivare "${item.name}"?`)) return;
    try {
      if (isClients) await arDeleteClient(item.id);
      else await arDeleteCategory(item.id);
      toast.success("Disattivato.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e, "Errore"));
    }
  };

  const reactivate = async (item) => {
    try {
      if (isClients) await arUpdateClient(item.id, { is_active: true });
      else await arUpdateCategory(item.id, { is_active: true });
      toast.success("Riattivato.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e, "Errore riattivazione"));
    }
  };

  const visibleItems = useMemo(() => {
    const q = normalize(query).trim();
    let list = items.filter((it) => (showArchived ? true : it.is_active));
    if (q) {
      list = list.filter((it) => {
        if (normalize(it.name).includes(q)) return true;
        if (isClients) {
          if (normalize(it.tax_code).includes(q)) return true;
          if (normalize(it.email).includes(q)) return true;
          if (normalize(it.phone).includes(q)) return true;
        }
        return false;
      });
    }
    return list;
  }, [items, showArchived, query, isClients]);

  return (
    <div data-testid={isClients ? "ar-clients-panel" : "ar-categories-panel"}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="relative min-w-[220px] flex-1 md:max-w-sm">
          <Search
            size={12}
            strokeWidth={2}
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400"
          />
          <input
            type="search"
            data-testid={isClients ? "search-ar-clients" : "search-ar-categories"}
            placeholder={
              isClients
                ? "Cerca cliente per nome, C.F./P.IVA, email…"
                : "Cerca categoria per nome…"
            }
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="h-8 w-full rounded-md border border-stone-200 bg-stone-50 pl-7 pr-7 text-[12px] text-stone-700 outline-none transition placeholder:text-stone-400 focus:border-primary/60 focus:bg-white focus:ring-2 focus:ring-primary/10"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              data-testid={isClients ? "search-ar-clients-clear" : "search-ar-categories-clear"}
              title="Pulisci ricerca"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-stone-400 hover:bg-stone-100 hover:text-stone-700"
            >
              <X size={11} strokeWidth={2} />
            </button>
          )}
        </div>
        <div className="flex items-center gap-3">
          <label
            data-testid={isClients ? "toggle-show-archived-clients" : "toggle-show-archived-categories"}
            className="flex cursor-pointer items-center gap-1.5 text-xs text-stone-600"
          >
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="h-3.5 w-3.5"
            />
            Mostra archiviati
          </label>
          <Button
            data-testid={isClients ? "btn-new-client" : "btn-new-ar-category"}
            onClick={openCreate}
          >
            + {isClients ? "Nuovo cliente" : "Nuova categoria incassi"}
          </Button>
        </div>
      </div>
      <div className="rounded-xl border border-stone-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              {isClients && <TableHead className="w-[160px]">Codice fiscale / P.IVA</TableHead>}
              {isClients && <TableHead className="w-[200px]">Email</TableHead>}
              <TableHead className="w-[100px]">Stato</TableHead>
              <TableHead className="w-[120px]">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleItems.length === 0 && (
              <TableRow>
                <TableCell colSpan={isClients ? 5 : 3} className="py-8 text-center text-sm text-stone-500">
                  {items.length === 0
                    ? "Nessuna voce. Crea la prima per iniziare."
                    : "Nessuna voce attiva. Spunta 'Mostra archiviati' per vederli."}
                </TableCell>
              </TableRow>
            )}
            {visibleItems.map((it) => (
              <TableRow key={it.id} data-testid={`ar-row-${it.id}`}>
                <TableCell>
                  <div className={"text-sm font-medium " + (it.is_active ? "text-stone-900" : "text-stone-400 line-through")}>
                    {it.name}
                  </div>
                </TableCell>
                {isClients && (
                  <TableCell className="text-xs text-stone-600">{it.tax_code || "—"}</TableCell>
                )}
                {isClients && (
                  <TableCell className="text-xs text-stone-600">{it.email || "—"}</TableCell>
                )}
                <TableCell>
                  <span
                    className={
                      "inline-flex rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider " +
                      (it.is_active
                        ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                        : "bg-stone-100 text-stone-500")
                    }
                  >
                    {it.is_active ? "Attivo" : "Archiviato"}
                  </span>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`ar-btn-edit-${it.id}`}
                      onClick={() => openEdit(it)}
                      className="h-7 px-2 text-xs"
                    >
                      <Pencil size={12} />
                    </Button>
                    {it.is_active ? (
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`ar-btn-delete-${it.id}`}
                        onClick={() => remove(it)}
                        className="h-7 px-2 text-xs text-rose-700"
                      >
                        Archivia
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`ar-btn-reactivate-${it.id}`}
                        onClick={() => reactivate(it)}
                        className="h-7 px-2 text-xs text-emerald-700"
                      >
                        Riattiva
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="ar-form-dialog">
          <DialogHeader>
            <DialogTitle>
              {editing ? "Modifica" : "Crea"} {isClients ? "cliente" : "categoria incassi"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nome</Label>
              <Input
                data-testid="ar-input-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>
            {isClients && (
              <>
                <div>
                  <Label>Codice fiscale / Partita IVA</Label>
                  <Input
                    data-testid="ar-input-taxcode"
                    value={form.tax_code}
                    onChange={(e) => setForm((f) => ({ ...f, tax_code: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    data-testid="ar-input-email"
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Telefono</Label>
                  <Input
                    data-testid="ar-input-phone"
                    value={form.phone}
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Metodo di pagamento preferito</Label>
                  <Select
                    value={form.default_payment_method_id || "__none__"}
                    onValueChange={(v) =>
                      setForm((f) => ({
                        ...f,
                        default_payment_method_id: v === "__none__" ? "" : v,
                      }))
                    }
                  >
                    <SelectTrigger data-testid="ar-input-default-method">
                      <SelectValue placeholder="Nessuno (usa default globale)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Nessuno</SelectItem>
                      {paymentMethods.map((pm) => (
                        <SelectItem key={pm.id} value={pm.id}>
                          {pm.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="mt-1 text-[10px] leading-snug text-stone-500">
                    Usato come proposta automatica in "Registra incasso"
                    quando la ricorrenza non specifica un metodo proprio.
                  </p>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button data-testid="ar-btn-submit" onClick={submit}>Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function ARClientsTab() {
  return <CrudPanel kind="clients" />;
}

export function ARCategoriesTab() {
  return <CrudPanel kind="categories" />;
}
